import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  createMedioDePago,
  listAllMediosDePago,
  reactivateMedioDePago,
  softDeleteMedioDePago,
  updateMedioDePago,
  type MedioDePagoItem,
} from '@/shared/api/controllers/mediosDePago';
import { invalidateAbmRelatedQueries } from '@/shared/api/abmCacheInvalidation';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { esCajaEfectivoConPar } from '@/shared/lib/cajasEfectivo';
import { CajaNombre } from '@/features/tesoreria/components/CajaNombre';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'medios-de-pago'] as const;

const TIPOS = ['Efectivo', 'Billetera Virtual', 'Banco', 'E-cheq'] as const;
type Tipo = (typeof TIPOS)[number];
const CATEGORIAS = ['Local', 'Internacional'] as const;
type Categoria = (typeof CATEGORIAS)[number];

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: MedioDePagoItem }
  | { kind: 'delete'; item: MedioDePagoItem };

const schema = z.object({
  nombre: z.string().trim().min(1, 'Ingresá el nombre de la caja'),
  categoria: z.enum(CATEGORIAS, { required_error: 'Seleccioná la categoría' }),
  tipo: z.enum(TIPOS, { errorMap: () => ({ message: 'Seleccioná el tipo' }) }),
  llevaFactura: z.boolean(),
  soloTesoreria: z.boolean(),
  cuotas: z.string().optional(),
});

type Values = z.infer<typeof schema>;

const empty: Values = {
  nombre: '',
  categoria: 'Local',
  tipo: 'Efectivo',
  llevaFactura: false,
  soloTesoreria: false,
  cuotas: '',
};

function itemToForm(item: MedioDePagoItem): Values {
  const f = item.fields;
  const categoria: Categoria =
    f.Categoria_MDP === 'Internacional' ? 'Internacional' : 'Local';
  const tipoRaw = (f.Tipo_MDP ?? '') as string;
  // Sin tipo cargado → vacío, para que lo elijan. Antes se completaba con
  // 'Efectivo' y guardar la caja lo dejaba grabado sin que nadie lo eligiera
  // (así quedaron Galicia, Credicoop y MercadoPago como 'Efectivo', 2026-07-29).
  const tipo = (TIPOS.includes(tipoRaw as Tipo) ? tipoRaw : '') as Tipo;
  return {
    nombre: f.Medio_MDP ?? '',
    categoria,
    tipo,
    llevaFactura: f.LlevaFactura_MDP === 'SI',
    soloTesoreria: f.SoloTesoreria_MDP === 'SI',
    cuotas: f.Cuotas_MDP ?? '',
  };
}

export function MediosDePagoEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllMediosDePago,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  // Caja Diferencia is the Nova margen bucket — auto-credited/debited by
  // the Cobrar Fornitalia confirm (PR 3.3.f). `emitCajaDiferencia`
  // resolves the payment_method_id by name, so renaming or soft-deleting
  // this row would silently leave the auto-writes without a
  // payment_method_id (rows would still post but MedioPago_MC would
  // render null). It still exists in the DB and is used by Tesorería +
  // auto-write paths — only the ABM view hides it.
  //
  // BBVA Italmexy IS user-facing on non-Nova ventas (cash/direct
  // channels in MX): it can be picked as a customer-payment medio in
  // those flows, so admins need to be able to edit/disable it from
  // here. It's only auto-handled internally inside the Nova flow's
  // inter-co Pagar/Cobrar step.
  const HIDDEN_FROM_ABM = new Set(['Caja Diferencia']);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows]
      .filter((r) => !HIDDEN_FROM_ABM.has((r.fields.Medio_MDP ?? '').trim()))
      .sort((a, b) =>
        (a.fields.Medio_MDP ?? '').localeCompare(b.fields.Medio_MDP ?? '', 'es'),
      );
    if (filterValue) {
      sorted = sorted.filter((r) => r.fields.Status_MDP === filterValue);
    }
    if (!q) return sorted;
    return sorted.filter((r) => {
      const n = (r.fields.Medio_MDP ?? '').toLowerCase();
      const t = (r.fields.Tipo_MDP ?? '').toLowerCase();
      return n.includes(q) || t.includes(q);
    });
  }, [rows, search, filterValue]);

  const columns: CrudColumn<MedioDePagoItem>[] = [
    {
      header: 'Status',
      width: 'w-28',
      render: (r) => <StatusBadge status={r.fields.Status_MDP} />,
    },
    {
      header: 'Caja',
      render: (r) => (
        <span className="inline-flex items-center gap-2 font-medium text-foreground">
          <CajaNombre caja={r.fields.Medio_MDP} soloMarca />
          {r.fields.SoloTesoreria_MDP === 'SI' && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              Sólo Tesorería
            </span>
          )}
        </span>
      ),
    },
    {
      header: 'Tipo',
      render: (r) => r.fields.Tipo_MDP || <span className="text-muted-foreground">—</span>,
    },
    {
      header: 'Moneda',
      width: 'w-28',
      render: (r) =>
        r.fields.Categoria_MDP === 'Internacional' ? (
          <span className="font-medium text-sky-700">USD</span>
        ) : (
          activeCountry.currency.primary
        ),
    },
    {
      header: 'Lleva Factura',
      width: 'w-32',
      render: (r) => (
        <span
          className={
            r.fields.LlevaFactura_MDP === 'SI'
              ? 'font-medium text-emerald-700'
              : 'text-muted-foreground'
          }
        >
          {r.fields.LlevaFactura_MDP === 'SI' ? 'Sí' : 'No'}
        </span>
      ),
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    invalidateAbmRelatedQueries(queryClient, 'medios-de-pago');
  };

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={search ? `No hay resultados para "${search}".` : 'Sin cajas.'}
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canDelete={(r) =>
          r.fields.Status_MDP === 'Activo' && !esCajaEfectivoConPar(r.fields.Medio_MDP ?? '')
        }
        deleteDisabledTitle={(r) =>
          esCajaEfectivoConPar(r.fields.Medio_MDP ?? '')
            ? 'Caja de efectivo con par (con / sin factura): no se puede dar de baja'
            : 'El registro ya está inactivo'
        }
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <MedioDePagoDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          existingRows={rows}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeleteMedioDePagoDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function MedioDePagoDialog({
  open,
  onOpenChange,
  item,
  existingRows,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: MedioDePagoItem | null;
  existingRows: MedioDePagoItem[];
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isEdit = Boolean(item);
  const { config: activeCountry } = useActiveCountry();
  // Las cajas de efectivo con par ("Efectivo Pesos" / "Efectivo Pesos N", ídem
  // Dolar) se reconocen POR NOMBRE y su "Lleva Factura" es la etiqueta que las
  // distingue (lib/cajasEfectivo). Renombrarlas o tocarles el flag rompe el
  // ruteo en silencio: nombre y switch quedan fijos.
  const esPar = isEdit && esCajaEfectivoConPar(item?.fields.Medio_MDP ?? '');
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) form.reset(item ? itemToForm(item) : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  // Banco rows default to Lleva Factura ON when the user PICKS Banco from the
  // tipo dropdown (and OFF when switching away). ONLY on a user-driven change —
  // NOT on dialog open. It used to run on open too and CLOBBER the saved value:
  // a medio with tipo≠Banco (e.g. empty→'Efectivo') + Lleva Factura Sí had it
  // silently turned OFF just by opening + guardando (bug 2026-07-29, disabled
  // Galicia/Credicoop/MercadoPago). The toggle stays editable for exceptions.
  const tipo = form.watch('tipo');
  const tipoSyncReady = useRef(false);
  useEffect(() => {
    if (esPar) return; // el flag del par no se sincroniza con el tipo
    // Skip the first run (dialog just opened / form reset to the item's saved
    // values) so we never override the persisted Lleva Factura.
    if (!tipoSyncReady.current) {
      tipoSyncReady.current = true;
      return;
    }
    if (tipo === 'Banco' && !form.getValues('llevaFactura')) {
      form.setValue('llevaFactura', true, { shouldDirty: true });
    }
    if (tipo !== 'Banco' && form.getValues('llevaFactura')) {
      form.setValue('llevaFactura', false, { shouldDirty: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  const mutation = useMutation({
    mutationFn: async (v: Values) => {
      const payload = {
        medio: v.nombre,
        categoria: v.categoria,
        tipo: v.tipo,
        llevaFactura: v.llevaFactura,
        soloTesoreria: v.soloTesoreria,
        cuotas: v.cuotas?.trim() || null,
      };
      if (isEdit && item) return updateMedioDePago(item.id, payload);
      return createMedioDePago(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit
          ? 'La caja se actualizó correctamente.'
          : 'La caja se creó correctamente.',
      ),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo guardar la caja.'),
  });

  const onSubmit = form.handleSubmit((v) => {
    const norm = v.nombre.trim().toLowerCase();
    const duplicated = existingRows.find(
      (r) =>
        r.id !== item?.id &&
        (r.fields.Medio_MDP ?? '').trim().toLowerCase() === norm,
    );
    if (duplicated) {
      form.setError('nombre', {
        type: 'manual',
        message: 'Ya existe una caja con ese nombre.',
      });
      return;
    }
    mutation.mutate(v);
  });

  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  const watchedNombre = form.watch('nombre');
  const watchedCategoria = form.watch('categoria');
  const watchedTipo = form.watch('tipo');
  const watchedLleva = form.watch('llevaFactura');
  const watchedSoloTesoreria = form.watch('soloTesoreria');

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!watchedNombre?.trim()) missing.push('Nombre');
    if (!watchedCategoria) missing.push('Moneda');
    if (!watchedTipo) missing.push('Tipo');
    return missing;
  }, [watchedNombre, watchedCategoria, watchedTipo]);

  const canSubmit = missingFields.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={
          mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando caja…')
        }
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Editar caja' : 'Nueva caja'}
          </DialogTitle>
          <DialogDescription>
            Definí el tipo, la moneda de la caja y si fuerza Lleva Factura al cargar pagos.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="nombre">
                Nombre <span className="text-primary">*</span>
              </Label>
              {esPar ? (
                // Nombre fijo y sin la "N" interna. El input no se monta, pero
                // react-hook-form conserva el valor por defecto (shouldUnregister
                // es false), así que el submit sigue mandando el nombre real.
                <div className="flex h-12 cursor-not-allowed items-center rounded-lg bg-secondary px-4 text-[15px] opacity-70">
                  <CajaNombre caja={item?.fields.Medio_MDP} />
                </div>
              ) : (
                <Input
                  id="nombre"
                  variant="filled"
                  placeholder="Ej: Transferencia Galicia"
                  autoFocus
                  {...form.register('nombre')}
                />
              )}
              {fieldError('nombre') && (
                <p className="text-xs font-medium text-destructive">{fieldError('nombre')}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>
                Tipo <span className="text-primary">*</span>
              </Label>
              <Select
                value={watchedTipo}
                onValueChange={(v) =>
                  form.setValue('tipo', v as Tipo, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldError('tipo') && (
                <p className="text-xs font-medium text-destructive">{fieldError('tipo')}</p>
              )}
            </div>

            {/* La moneda de la caja se guarda como Categoría (Internacional =
                dólares): es lo que lee todo el sistema, no el nombre. */}
            <div className="flex items-start justify-between gap-4 rounded-xl border bg-secondary/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="cajaUsd" className="text-sm font-medium">
                  Caja en dólares (USD)
                </Label>
                <p className="text-xs text-muted-foreground">
                  {watchedCategoria === 'Internacional'
                    ? 'Los montos de esta caja están en dólares.'
                    : `Los montos de esta caja están en ${activeCountry.currency.primary}.`}{' '}
                  {isEdit
                    ? 'Si la caja ya tiene movimientos no se puede cambiar: creá una nueva.'
                    : 'Elegilo bien: una vez que tenga movimientos no se puede cambiar.'}
                </p>
              </div>
              <Switch
                id="cajaUsd"
                checked={watchedCategoria === 'Internacional'}
                onCheckedChange={(v) =>
                  form.setValue('categoria', v ? 'Internacional' : 'Local', {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cuotas">Cuotas (opcional)</Label>
              <Input
                id="cuotas"
                variant="filled"
                placeholder="Ej: 3, 6, 12"
                {...form.register('cuotas')}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border bg-secondary/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="llevaFactura" className="text-sm font-medium">
                  Forzar "Lleva Factura"
                </Label>
                <p className="text-xs text-muted-foreground">
                  {esPar
                    ? 'Caja de efectivo con par: la que lleva factura y su gemela "N" sin factura. El sistema elige entre las dos según el cobro o la compra, por eso el nombre y este valor no se pueden cambiar.'
                    : watchedTipo === 'Banco'
                      ? 'Por defecto los bancos siempre llevan factura. Desactivá este toggle solo para excepciones (p. ej. Morvalab).'
                      : 'No suele aplicarse a este tipo. Activalo solo si querés que el toggle quede forzado al cargar un pago.'}
                </p>
              </div>
              <Switch
                id="llevaFactura"
                disabled={esPar}
                checked={watchedLleva}
                onCheckedChange={(v) =>
                  form.setValue('llevaFactura', v, { shouldDirty: true })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-xl border bg-secondary/30 px-4 py-3">
              <div className="space-y-0.5">
                <Label htmlFor="soloTesoreria" className="text-sm font-medium">
                  Sólo Tesorería
                </Label>
                <p className="text-xs text-muted-foreground">
                  Caja interna (p. ej. un FCI): las vendedoras no la ven al cargar cobros
                  de una venta. Tesorería la sigue usando normalmente.
                </p>
              </div>
              <Switch
                id="soloTesoreria"
                checked={watchedSoloTesoreria}
                onCheckedChange={(v) =>
                  form.setValue('soloTesoreria', v, { shouldDirty: true })
                }
              />
            </div>
          </DialogBody>
          <DialogFooter>
            {missingFields.length > 0 && (
              <p className="mr-auto text-xs text-muted-foreground">
                Faltan completar:{' '}
                <span className="font-medium text-destructive">
                  {missingFields.join(', ')}
                </span>
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !canSubmit}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteMedioDePagoDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: MedioDePagoItem;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeleteMedioDePago(item.id),
    onSuccess: () => onSuccess('Caja dada de baja.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo dar de baja la caja.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Dando de baja…'}
      >
        <DialogHeader>
          <DialogTitle>Dar de baja caja</DialogTitle>
          <DialogDescription>
            ¿Confirmás dar de baja{' '}
            <strong className="text-foreground">{item.fields.Medio_MDP || '—'}</strong>?
            Las ventas / compras existentes que ya lo usen no se verán afectadas.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            Dar de baja
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-exported in case a future flow needs to reactivate from outside.
export { reactivateMedioDePago };
